import React, { useState, useMemo } from 'react';
import { Stack, IconButton, Tooltip, Box } from '@mui/material';
import { ContentCopy, Check, Refresh } from '@mui/icons-material';
import { candidateProfileSearchConversationSx } from 'modules/candidate-profile/styles';
import { useToast } from 'services/toast';
import { ToastType } from 'services/toast/types';
import {
  parseMessageWithCandidatesAndChips,
  CandidateProfileLinkData
} from 'modules/candidate-profile/utils/parseMessageWithCandidatesAndChips';
import { convertMessageToPlainText } from 'modules/candidate-profile/utils/convertMessageToPlainText';

type CandidateProfileAgentMessageProps = {
  messageId: string;
  messageBody: string;
  candidateProfiles?: CandidateProfileLinkData[];
  onRegenerate: () => void;
  isLoading: boolean;
  onChipClick?: (suggestion: string, field?: string) => void;
  searchParams?: Record<string, string>;
};

export const CandidateProfileAgentMessage: React.FC<
  CandidateProfileAgentMessageProps
> = ({
  messageBody,
  candidateProfiles = [],
  onRegenerate,
  isLoading,
  onChipClick,
  searchParams = {}
}) => {
  const { showToast } = useToast();
  const [isCopied, setIsCopied] = useState(false);

  // Parse the message body to replace UUIDs with candidate names/links and suggestions with chips
  const parsedMessage = useMemo(
    () =>
      parseMessageWithCandidatesAndChips({
        messageBody,
        candidateProfiles,
        onChipClick,
        searchParams
      }),
    [messageBody, candidateProfiles, onChipClick, searchParams]
  );

  const handleCopyMessage = async () => {
    try {
      const plainText = convertMessageToPlainText({
        messageBody,
        candidateProfiles
      });
      await navigator.clipboard.writeText(plainText);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      showToast({
        message: 'Text copied to clipboard',
        type: ToastType.Success
      });
    } catch (error) {
      showToast({
        message: 'Failed to copy text',
        type: ToastType.Error
      });
    }
  };

  return (
    <>
      <Box sx={candidateProfileSearchConversationSx.conversationAgentMessage}>
        {parsedMessage}
      </Box>
      <Stack
        direction="row"
        gap={1}
        sx={
          candidateProfileSearchConversationSx.conversationAgentMessageActions
        }
      >
        <Tooltip title="Regenerate from here">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={onRegenerate}
              disabled={isLoading}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isCopied ? 'Copied!' : 'Copy to clipboard'}>
          <IconButton size="small" color="primary" onClick={handleCopyMessage}>
            {isCopied ? (
              <Check fontSize="small" />
            ) : (
              <ContentCopy fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </>
  );
};
